import { queryKeys } from '@query/keys'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useFormikContext } from 'formik'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { AddressLookupMobileModal } from '@pages/Signup/Pages/PersonalDetails/PersonalDetailsStep4/AddressLookupMobile.modal'
import type { PersonalDetailsStep4FormValues } from '@pages/Signup/Pages/PersonalDetails/PersonalDetailsStep4/PersonalDetailsStep4Form'

import { useModal } from '@context/ModalsContext'

import { useApiClient } from '@utils/ApiClient'
import {
  ADDRESS_LOOKUP_DEBOUNCE_MS,
  AddressEntryMode,
  getAddressRegionCode,
  normalizeAddressValue,
} from '@utils/address.utils'
import { ClientApiClient } from '@utils/clientApi'
import { useDebouncedValue } from '@utils/debounce'
import { useWindowResize } from '@utils/domUtils'
import { removeRestrictedSpecialCharacters } from '@utils/validations'

import { AddressAutocompleteSuggestion, PlaceAddressDetails } from '@model/AddressAutocomplete'

interface UseResidentialAddressLookupProps {
  countryId?: string
}

export const useResidentialAddressLookup = ({ countryId }: UseResidentialAddressLookupProps) => {
  const isMobile = useWindowResize()
  const apiClient = useApiClient(ClientApiClient)

  const { values, setFieldTouched, setFieldValue, setValues, validateForm } =
    useFormikContext<PersonalDetailsStep4FormValues>()

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const isLookup = values.addressEntryMode === AddressEntryMode.Lookup
  const regionCode = getAddressRegionCode(countryId)
  const debouncedAddress = useDebouncedValue(values.address, ADDRESS_LOOKUP_DEBOUNCE_MS)
  const hasAddressValue = Boolean(values.address.trim())
  const lookupQuery = debouncedAddress.trim()
  const isWaitingForDebouncedAddress = hasAddressValue && values.address.trim() !== lookupQuery

  const addressAutocompleteQuery = useQuery({
    queryKey: queryKeys.signup.addressAutocomplete(lookupQuery, regionCode),
    queryFn: () => apiClient.autocompleteAddress(lookupQuery, [regionCode as string]),
    enabled: isLookup && Boolean(lookupQuery) && Boolean(regionCode),
    retry: false,
  })

  const { mutateAsync: getPlaceAddressDetails, isPending: isPlaceAddressDetailsLoading } =
    useMutation({
      mutationFn: (placeId: string) => apiClient.getPlaceAddressDetails(placeId),
      onError: () => undefined,
    })

  const suggestions = useMemo(() => {
    if (!isLookup || isWaitingForDebouncedAddress || !regionCode) return []
    return addressAutocompleteQuery.data?.suggestions || []
  }, [
    addressAutocompleteQuery.data?.suggestions,
    isLookup,
    isWaitingForDebouncedAddress,
    regionCode,
  ])

  const hasLookupRun =
    hasAddressValue &&
    !isWaitingForDebouncedAddress &&
    (!regionCode || addressAutocompleteQuery.isFetched)

  const isLookupLoading =
    isLookup &&
    hasAddressValue &&
    (isWaitingForDebouncedAddress ||
      addressAutocompleteQuery.isFetching ||
      isPlaceAddressDetailsLoading)

  const canShowManualOption = isLookup && hasLookupRun && hasAddressValue

  const canShowDropdown =
    !isMobile &&
    isLookup &&
    isDropdownOpen &&
    !isLookupLoading &&
    (suggestions.length > 0 || canShowManualOption)

  const showLookupLoader = isLookup && hasAddressValue && (isLookupLoading || !hasLookupRun)

  const touchAddressFields = useCallback(
    async (touched: boolean) => {
      await Promise.all([
        setFieldTouched('address', touched, false),
        setFieldTouched('streetHouseNumber', touched, false),
        setFieldTouched('primaryAddressCity', touched, false),
        setFieldTouched('primaryAddressState', touched, false),
        setFieldTouched('primaryAddressPostalCode', touched, false),
      ])
    },
    [setFieldTouched]
  )

  const getClearedAddressValues = useCallback(
    (preserveAddress: boolean, addressEntryMode = values.addressEntryMode) => {
      return {
        ...values,
        address: preserveAddress ? values.address : '',
        streetHouseNumber: '',
        streetHouseNumberNotAvailable: false,
        primaryAddressCity: '',
        primaryAddressState: '',
        primaryAddressPostalCode: '',
        addressEntryMode,
      }
    },
    [values]
  )

  const switchToManualEntry = useCallback(
    async (preserveAddress: boolean) => {
      setIsDropdownOpen(false)

      const nextValues = getClearedAddressValues(preserveAddress, AddressEntryMode.Manual)
      await setValues(nextValues, false)
      await touchAddressFields(false)
      await validateForm(nextValues)
    },
    [getClearedAddressValues, setValues, touchAddressFields, validateForm]
  )

  const populateSelectedAddress = useCallback(
    async (details: PlaceAddressDetails) => {
      const nextValues = {
        ...values,
        address: normalizeAddressValue(details.street),
        streetHouseNumber: normalizeAddressValue(details.houseNumber),
        streetHouseNumberNotAvailable: false,
        primaryAddressCity: normalizeAddressValue(details.city),
        primaryAddressState: normalizeAddressValue(details.state),
        primaryAddressPostalCode: normalizeAddressValue(details.postalCode),
        addressEntryMode: AddressEntryMode.Selected,
      }

      setIsDropdownOpen(false)
      await setValues(nextValues, false)
      await touchAddressFields(true)
      await validateForm(nextValues)
    },
    [setValues, touchAddressFields, validateForm, values]
  )

  const handleAddressValueChange = useCallback(
    (nextValue: string) => {
      const value = removeRestrictedSpecialCharacters(nextValue)
      setFieldValue('address', value)
      if (!isLookup) return
      setIsDropdownOpen(Boolean(value.trim()))
    },
    [isLookup, setFieldValue]
  )

  const handleSelectSuggestion = useCallback(
    async (suggestion: AddressAutocompleteSuggestion) => {
      setIsDropdownOpen(false)

      try {
        const details = await getPlaceAddressDetails(suggestion.placeId)
        await populateSelectedAddress(details)
      } catch {
        await switchToManualEntry(true)
      }
    },
    [getPlaceAddressDetails, populateSelectedAddress, switchToManualEntry]
  )

  const handleManualEntry = useCallback(() => {
    void switchToManualEntry(false)
  }, [switchToManualEntry])

  const handleStreetAvailabilityChange = useCallback(
    async (checked: boolean) => {
      const nextValues = {
        ...values,
        streetHouseNumberNotAvailable: checked,
        streetHouseNumber: checked ? '' : values.streetHouseNumber,
      }

      await setValues(nextValues, false)
      await setFieldTouched('streetHouseNumber', !checked, false)
      await validateForm(nextValues)
    },
    [setFieldTouched, setValues, validateForm, values]
  )

  const {
    isOpen: isMobileLookupModalOpen,
    open: openMobileLookupModal,
    close: closeMobileLookupModal,
    reRender: reRenderMobileLookupModal,
  } = useModal(AddressLookupMobileModal, {
    value: values.address,
    isLoading: showLookupLoader,
    suggestions,
    showManualOption: canShowManualOption,
    onChange: handleAddressValueChange,
    onClear: () => handleAddressValueChange(''),
    onSelectSuggestion: handleSelectSuggestion,
    onSelectManualEntry: handleManualEntry,
  })

  useEffect(() => {
    if (!isMobileLookupModalOpen) return

    reRenderMobileLookupModal()
  }, [
    canShowManualOption,
    isMobileLookupModalOpen,
    reRenderMobileLookupModal,
    showLookupLoader,
    suggestions,
    values.address,
  ])

  useEffect(() => {
    if (
      !isLookup ||
      !hasAddressValue ||
      isWaitingForDebouncedAddress ||
      !addressAutocompleteQuery.isError
    ) {
      return
    }

    void switchToManualEntry(true)
  }, [
    addressAutocompleteQuery.isError,
    hasAddressValue,
    isLookup,
    isWaitingForDebouncedAddress,
    switchToManualEntry,
  ])

  useEffect(() => {
    if (isMobile && isLookup) return
    if (isMobileLookupModalOpen) closeMobileLookupModal()
  }, [closeMobileLookupModal, isLookup, isMobile, isMobileLookupModalOpen])

  return {
    isLookup,
    isStreetHouseNumberNotAvailable: values.streetHouseNumberNotAvailable,
    suggestions,
    canShowManualOption,
    canShowDropdown,
    showLookupLoader,
    setIsDropdownOpen,
    openMobileLookupModal,
    handleAddressValueChange,
    handleSelectSuggestion,
    handleManualEntry,
    handleStreetAvailabilityChange,
  }
}
